import { gql, useQuery } from "@apollo/client";
import Errors from "components/base/Errors";
import { Select, Switcher } from "components/base/Form";
import Button from "components/base/Button";
import ShipmentErrorComponent, {
  isCustomerRelated,
} from "pages/shipment/components/ShipmentError";
import Spinner from "components/base/Spinner";
import { useModals } from "ModalProvider";
import { useState } from "react";
import { BiUserCircle } from "react-icons/bi";
import { FaExternalLinkAlt } from "react-icons/fa";
import { Link } from "react-router-dom";

const FETCH_SHIPMENT_ERRORS = gql`
  query FETCH_SHIPMENT_ERRORS($byPeriod: String!, $years: Int!) {
    shipmentErrorsAnalytics(byPeriod: $byPeriod, years: $years)
  }
`;

const FETCH_ERROR_CODES = gql`
  query FETCH_ERROR_CODES {
    errorCodes: allErrors {
      id
      code: fullCode
      description
    }
  }
`;

const ErrorCodes = () => {
  const { loading, error, data } = useQuery(FETCH_ERROR_CODES);

  if (error) return <Errors error={error} />;
  if (loading) return <Spinner />;

  return (
    <div className="m-4 h-[60vh] overflow-auto rounded-lg border border-gray-200 lg:m-6">
      <table className="bg-white text-sm">
        <thead className="bg-gray-200">
          <tr>
            <th align="left" className="p-4 px-6">
              Code
            </th>
            <th align="left" className="px-6 py-2">
              Description
            </th>
          </tr>
        </thead>
        <tbody>
          {data.errorCodes.map((errorCode) => (
            <tr key={errorCode.id}>
              <td className="border-b border-gray-100 px-6 py-2 font-bold text-pink-500">
                {errorCode.code}
              </td>
              <td className="flex items-center border-b border-gray-100 px-6 py-2 text-gray-600">
                <ShipmentErrorComponent
                  code={errorCode.code}
                  description={errorCode.description}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ErrorsView = () => {
  const [period, setPeriod] = useState("quarter");
  const [years, setYears] = useState(2);
  const [showDetails, setShowDetails] = useState(true);
  const modal = useModals();
  const { loading, error, data } = useQuery(FETCH_SHIPMENT_ERRORS, {
    variables: {
      byPeriod: period,
      years,
    },
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const shipmentErrors = JSON.parse(data.shipmentErrorsAnalytics);

  return (
    <div className="card">
      <div className="flex justify-between items-baseline">
        <h4>Shipment Errors</h4>
        <div className="flex items-center space-x-6">
          <Select value={period} onChange={(e) => setPeriod(e.target.value)}>
            <option value="quarter">by Quarter</option>
            <option value="year">by Year</option>
          </Select>
          <Select
            value={years}
            onChange={(e) => setYears(Number(e.target.value))}
          >
            <option value={2}>Past 2 years</option>
            <option value={3}>Past 3 years</option>
          </Select>
          <div className="flex items-center space-x-2">
            <label htmlFor="">Show detail: </label>
            <Switcher
              isOn={showDetails}
              onChange={() => setShowDetails(!showDetails)}
            />
          </div>
          <Button
            onClick={() =>
              modal.present({
                title: "Error Codes",
                children: <ErrorCodes />,
              })
            }
          >
            Error Codes
          </Button>
        </div>
      </div>
      <div className="mt-2 flex flex-col gap-6 text-sm">
        {shipmentErrors.map((i) => (
          <section key={i.period} className="flex flex-col gap-4">
            <div>
              <h5>
                {i.period} - {i.error_rates}%
              </h5>
              <div className="opacity-70">
                {i.shipments_with_errors_count} shipments with a total of{" "}
                {i.errors_count} errors out of {i.period_shipments_count}{" "}
                shipments delivered to port in this period.
              </div>
            </div>

            <div className="flex flex-col gap-4">
              {i.errors.map((error) => (
                <div key={error.id} className="flex items-baseline gap-4">
                  <div className="w-12 shrink-0 font-bold text-pink-500">
                    {error.code}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center space-x-1">
                      <div className="font-bold opacity-70">
                        {error.description}
                      </div>
                      {isCustomerRelated(error.code) && (
                        <BiUserCircle className=" text-orange-600" size={18} />
                      )}
                    </div>
                    {showDetails && (
                      <div className="mt-2 flex flex-col gap-2 text-xs">
                        {error.shipment_errors.map(
                          ({
                            shipment,
                            products,
                            factories,
                            delay_days,
                            id,
                          }) => (
                            <div key={id} className="space-y-1">
                              <div className="flex space-x-4 items-center">
                                <Link
                                  to={`/shipment/shipments/${shipment.id}`}
                                  target="_blank"
                                  className="flex items-center space-x-1"
                                >
                                  <span>
                                    #{shipment.id} {shipment.invoice_number}
                                  </span>
                                  <span>
                                    <FaExternalLinkAlt size={9} />
                                  </span>
                                </Link>
                                {delay_days > 0 && (
                                  <div>{delay_days} days delayed</div>
                                )}
                              </div>
                              {products.length > 0 && (
                                <div>
                                  <span>Products: </span>
                                  {products.map((x) => x.number).join(", ")}
                                </div>
                              )}

                              {factories.length > 0 && (
                                <div>
                                  <span>Factories: </span>
                                  {factories.map((x) => x.name).join(", ")}
                                </div>
                              )}
                            </div>
                          ),
                        )}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 text-lg">
                    &times; {error.error_count}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default ErrorsView;
